import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { adoptLightStyles } from '@/internal/adopt-light-styles'
import { DdsElement } from '@/internal/dds-hu-element'
import { LabelableMixin } from '@/internal/mixin/labelableMixin'
import { SizedMixin } from '@/internal/mixin/sizedMixin'

import styles from './_styles/form-label.scss?inline'

/**
 * `dap-ds-form-label`
 * @summary A form label is a container for labels in a form.
 * @element dap-ds-form-label
 * @title - Form label
 * @group form
 *
 * @property {string} optionalLabel - Label of optional text
 * @property {boolean} subtle - Text weight of the label.
 * @property {boolean} optional - If the label is optional.
 * @property {boolean} required - If the label is required.
 * @property {string} label - The label text.
 *
 * @csspart base - The main form label container.
 * @csspart required - The required indicator of the form label.
 * @csspart optional - The optional indicator of the form label.
 *
 * @cssproperty --dds-form-label-font-weight - Font weight of the label. (default: var(--dds-font-weight-bold))
 * @cssproperty --dds-form-label-line-height - Line height of the label. (default: var(--dds-font-line-height-xlarge))
 * @cssproperty --dds-form-label-color - Text color of the label. (default: var(--dds-text-neutral-enabled))
 * @cssproperty --dds-form-label-disabled-color - Text color when the label is disabled. (default: var(--dds-text-neutral-disabled))
 * @cssproperty --dds-form-label-required-color - Color of the required indicator. (default: var(--dds-text-negative-subtle))
 * @cssproperty --dds-form-label-optional-color - Color of the optional indicator. (default: var(--dds-text-neutral-subtle))
 * @cssproperty --dds-form-label-margin-bottom - Bottom margin of the label. (default: var(--dds-spacing-200))
 */
@customElement('dap-ds-form-label')
export default class DapDSFormLabel extends SizedMixin(
  LabelableMixin(DdsElement),
) {
  /** The id of the form element that the label is associated with. */
  @property() for?: string
  /** Whether the label should be rendered as text or a <label> tag. Default is label. */
  @property({ type: String }) renderAs: string = 'label'

  /** Whether the label should have no margin. */
  @property({ type: Boolean }) noMargin = false

  static readonly styles = unsafeCSS(styles)

  createRenderRoot() {
    adoptLightStyles(this)
    return this
  }

  render() {
    return html`
    
    <${unsafeStatic(this.renderAs)}
          part="label-base"
          class=${clsx('dds-label', `dds-label--${this.effectiveSize}`, {
            'dds-label--disabled': this.disabled,
            'dds-label--required': this.required,
            'dds-label--optional': this.optional,
            'dds-label--subtle': this.subtle,
            'dds-label--no-margin': this.noMargin,
          })}
          for=${ifDefined(this.for)}>
          ${this.label}
          ${
            this.required
              ? html`<span
                  part="required"
                  aria-hidden="true"
                  class="dds-label__required-indicator"
                  >*</span
                >`
              : null
          }
          ${
            this.optional
              ? html`<span
                  part="optional"
                  class=${clsx('dds-label__optional-indicator', {
                    [`dds-label__optional-indicator--${this.effectiveSize}`]:
                      this.effectiveSize,
                  })}>
                  ${this.optionalLabel}
                </span>`
              : null
          }
        </${unsafeStatic(this.renderAs)}>`
  }
}
